/* @flow */
import gql from 'graphql-tag';
import {pick} from 'lodash';
import {fragments as hitCollectionFragments} from '../components/HitCollection/HitCollection';
import {fragments as mapFragments} from '../components/Map/Map';
import {fragments as pagerFragments} from '../components/Pager/Pager';
import {fragments as percentBookedContentsFragments} from '../components/SearchBannerContainer/percentBookedContents';
import {fragments as urgentMessageContentsFragments} from '../components/SearchBannerContainer/urgentMessageContents';
import {fragments as destinationMessage} from '../components/SearchBannerContainer/destinationMessage';
import {fragments as footerSeoLinksFragments} from '../components/ConnectedFooterSeoLinks/ConnectedFooterSeoLinks';
import {fragments as filterCountsFragments} from '../../universal/queries/filteredCountQuery';
import {fragments as expandedSearchGroupsFragments} from '../components/ExpandedSearchGroups/ExpandedSearchGroups';
import {fragments as htmlTitleFragments} from '../components/HtmlTitle/HtmlTitle';

import {ResultsParams} from '../../server/graphql/params';

import type {SearchParamsType} from '../reducers/searchParams';
import type {FilterGroupType} from '../components/MoreFilters/MoreFilters';
import type {SERPHitShape, ListingShape} from '../components/Hit/Hit';
import type {MapViewport} from '../components/Map/Map';
import type {Shape as ExpandedSearchGroupsShape} from '../components/ExpandedSearchGroups/ExpandedSearchGroups';
import type DestinationMessage from '@homeaway/graphql-traveler-urgency-messaging';
import type {AlternativeLink} from '../components/HtmlTitle/HtmlTitle';

export type SearchReview = {
    detailPageUrl: string,
    reviewHead: string,
    reviewText: string,
    reviewCreated: string,
    reviewArrival: string,
    reviewRating: number,
    hitThumbnailUrl: string
};

export type SearchTerm = {
    description: string,
    name: string,
    termId: string
};

export type Coordinates = {
    latitude: number,
    longitude: number
};

export type SearchGeography = {
    name?: string,
    description?: string,
    geoType?: string,
    location?: Coordinates,
    isGeoCoded?: boolean
};

type Internal = {
    searchServiceUrl: string
};

export type HitCollection = {
    mapViewport?: MapViewport,
    hits?: SERPHitShape[],
    pageSize?: number,
    page?: number,
    pageCount?: number,
    expandedSearchGroups?: ExpandedSearchGroupsShape
};

export type BreadcrumbShape = {
    url: string,
    name: string
};

export type DestinationBreadcrumbShape = {
    name: string,
    url: string
};

export type DestinationShape = {
    breadcrumbs?: BreadcrumbShape[],
    links?: BreadcrumbShape[]
};

export type SearchQueryResults = {
    analyticsdatalayer?: string,
    breadcrumbs?: BreadcrumbShape[],
    destination?: DestinationShape,
    destinationBreadcrumbs?: DestinationBreadcrumbShape[],
    urgencyDestinationMessage?: DestinationMessage,
    hitCollection?: any,
    headlineTitle?: string,
    internal?: Internal,
    liveRegionRedirectUrl?: string,
    pidRedirectUrl?: string,
    hitCollection?: HitCollection,
    listings?: ListingShape[],
    pageTitle?: string,
    metaDescription?: string,
    metaKeywords?: string,
    searchBanner?: any,
    searchTerm?: ?SearchTerm,
    searchReviewsHeadline?: string,
    searchReviews?: SearchReview[],
    seoSerpLinks?: BreadcrumbShape[],
    filterGroups?: FilterGroupType[],
    canonicalLink?: String,
    alternativeLinks?: AlternativeLink[],
    searchGeography?: SearchGeography
};

export type SearchQueryResponseData = {
    results: ?SearchQueryResults,
    error?: Error,
    loading: boolean,
    variables: {params: SearchParamsType, url: string}
};

export const srsParams = (params: SearchParamsType) =>
    pick(params, ResultsParams);

export const searchQueryVariables = (params: SearchParamsType, url: string) => {
    let include = ['searchBanner', 'percentBooked'];

    return {
        params: {
            ...srsParams(params),
            include
        },
        url
    };
};

export const FORCE_GRAPHQL_ERROR_HEADER = 'ha-serp-force-graphql-err';

const searchQuery = gql`
    query SearchQuery($params: ResultsParams, $url: String) {
        results(params: $params) {
            id
            analyticsdatalayer
            destinationBreadcrumbs {
                name
                url
            }
            headlineTitle
            hitCollection {
                ...ExpandedSearchGroupsHitCollection
                ...PagerHitCollection
            }
            internal {
                searchServiceUrl
            }
            searchBanner {
                type
                ...PercentBookedSearchBanner
                ...UrgentMessageSearchBanner
            }
            breadcrumbs {
                url
                name
                termId
            }
            liveRegionRedirectUrl
            pidRedirectUrl
            searchReviewsHeadline
            searchReviews {
                detailPageUrl
                reviewHead
                reviewText
                reviewCreated
                reviewArrival
                reviewRating
                hitThumbnailUrl
            }
            searchTerm {
                description
                name
                termId
            }
            seoSerpLinks {
                name
                url
                termId
            }
            filterGroups {
                key
                filters {
                    key
                    checked
                }
            }
            searchGeography {
                geoType
                location {
                    latitude
                    longitude
                }
                isGeoCoded
            }
            ...DestinationMessageResults
            ...FilterCountsResults
            ...FooterSeoLinksResults
            ...HitCollectionResults
            ...HtmlTitleResults
            ...MapResults
        }
    }
    ${hitCollectionFragments.results}
    ${expandedSearchGroupsFragments.hitCollection}
    ${filterCountsFragments.results}
    ${footerSeoLinksFragments.results}
    ${mapFragments.results}
    ${pagerFragments.hitCollection}
    ${htmlTitleFragments.results}
    ${destinationMessage.results}
    ${percentBookedContentsFragments.searchBanner}
    ${urgentMessageContentsFragments.searchBanner}
`;

export default searchQuery;
